// packages/core/src/lib/poll/pollLocalStorage.ts
// Low-level localStorage operations for polls, responses, and votes
// Extracted from pollStorage.ts for better modularity and maintainability

import type { Poll, FormResponse, Vote } from "./pollTypes";
import { readFromStorage, writeToStorage } from "../storage/storageUtils";
import { handleError, logError } from "../error-handling";
import { logger } from "../logger";
import { isDatePoll } from "./pollValidation";

// ============================================================================
// STORAGE KEYS
// ============================================================================

export const STORAGE_KEY = "doodates_polls";
export const FORM_STORAGE_KEY = "doodates_form_polls";
export const FORM_RESPONSES_KEY = "doodates_form_responses";
export const VOTES_STORAGE_KEY = "doodates_votes";

// ============================================================================
// MEMORY CACHES
// ============================================================================

// Module-level caches to improve stability in test environments
// - memoryPollCache helps immediate lookups after addPoll() even if another test
//   clears localStorage concurrently.
export const memoryPollCache = new Map<string, Poll>();
export const memoryResponsesCache = new Map<string, FormResponse>();
export let memoryResponses: FormResponse[] = [];

// ============================================================================
// ENVIRONMENT DETECTION
// ============================================================================

/**
 * Check if we're in a browser environment (safe for tests)
 */
export function hasWindow(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

// ============================================================================
// POLL STORAGE OPERATIONS
// ============================================================================

/**
 * Read all polls from localStorage (unified, all types)
 * Returns empty array if storage is unavailable or corrupted
 */
export function getAllPolls(): Poll[] {
  try {
    const parsed = readFromStorage(STORAGE_KEY, memoryPollCache, []);
    return parsed;
  } catch (error) {
    const storageError = handleError(
      error,
      {
        component: "pollLocalStorage",
        operation: "getAllPolls",
      },
      "Erreur lors de la lecture des sondages",
    );

    logError(storageError, {
      component: "pollLocalStorage",
      operation: "getAllPolls",
    });

    return [];
  }
}

/**
 * Get only "standard" polls (date polls)
 * For backward compatibility where getPolls() was used for date polls only
 */
export function getPolls(): Poll[] {
  return getAllPolls().filter((p) => isDatePoll(p));
}

/**
 * Write polls to localStorage
 * Deduplicates polls by ID before saving
 */
export function savePolls(polls: Poll[]): void {
  const deduplicated = deduplicatePolls(polls);
  if (deduplicated.length !== polls.length) {
    logger.info(`Removed ${polls.length - deduplicated.length} duplicate polls`, "poll");
  }
  writeToStorage(STORAGE_KEY, deduplicated, memoryPollCache);
}

/**
 * Deduplicate polls by ID (keep the most recent)
 */
export function deduplicatePolls(polls: Poll[]): Poll[] {
  const seen = new Map<string, Poll>();
  for (const poll of polls) {
    const existing = seen.get(poll.id);
    if (!existing) {
      seen.set(poll.id, poll);
    } else {
      // Garder le plus récent (updated_at ou created_at)
      const existingDate = new Date(existing.updated_at || existing.created_at).getTime();
      const currentDate = new Date(poll.updated_at || poll.created_at).getTime();
      if (currentDate > existingDate) {
        logger.warn(`Duplicate poll ID found: ${poll.id}, keeping newer version`, "poll");
        seen.set(poll.id, poll);
      } else {
        logger.warn(`Duplicate poll ID found: ${poll.id}, keeping existing version`, "poll");
      }
    }
  }
  return Array.from(seen.values());
}

// ============================================================================
// FORM RESPONSE STORAGE OPERATIONS
// ============================================================================

/**
 * Read all form responses from localStorage
 * Merges with in-memory cache to avoid duplicates
 */
export function readAllResponses(): FormResponse[] {
  try {
    const fromStorage = readFromStorage(FORM_RESPONSES_KEY, memoryResponsesCache, []);
    // Merge with in-memory cache (avoid duplicates by id)
    const byId = new Map<string, FormResponse>();
    for (const r of memoryResponses) byId.set(r.id, r);
    for (const r of fromStorage) byId.set(r.id, r);
    const merged = Array.from(byId.values());
    // Keep memory cache in sync
    memoryResponses = merged;
    return merged;
  } catch (error) {
    const readError = handleError(
      error,
      {
        component: "pollLocalStorage",
        operation: "readAllResponses",
      },
      "Erreur lors de la lecture des réponses",
    );

    logError(readError, {
      component: "pollLocalStorage",
      operation: "readAllResponses",
    });

    // Fallback to memory cache if JSON parse fails or other issue
    return memoryResponses.slice();
  }
}

/**
 * Write all form responses to localStorage
 * Updates memory cache first (write-through)
 */
export function writeAllResponses(resps: FormResponse[]): void {
  // Update memory cache first (write-through)
  memoryResponses = resps.slice();
  writeToStorage(FORM_RESPONSES_KEY, resps, memoryResponsesCache);
}

// ============================================================================
// VOTE STORAGE OPERATIONS
// ============================================================================

/**
 * Read all votes from localStorage
 * Returns empty array if storage is unavailable or corrupted
 */
export function getAllVotes(): Vote[] {
  try {
    if (!hasWindow()) return [];
    const stored = localStorage.getItem(VOTES_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    const storageError = handleError(
      error,
      {
        component: "pollLocalStorage",
        operation: "getAllVotes",
      },
      "Erreur lors de la lecture des votes",
    );

    logError(storageError, {
      component: "pollLocalStorage",
      operation: "getAllVotes",
    });

    return [];
  }
}

/**
 * Write votes to localStorage
 */
export function saveVotes(votes: Vote[]): void {
  try {
    if (!hasWindow()) return;
    localStorage.setItem(VOTES_STORAGE_KEY, JSON.stringify(votes));
  } catch (error) {
    const handledError = handleError(
      error,
      {
        component: "pollLocalStorage",
        operation: "saveVotes",
      },
      "Erreur lors de la sauvegarde des votes",
    );
    throw handledError;
  }
}

// ============================================================================
// TEST UTILITIES
// ============================================================================

/**
 * Reset all memory caches (for testing purposes)
 */
export function resetMemoryStateForTests(): void {
  memoryPollCache.clear();
  memoryResponses = [];
  memoryResponsesCache.clear();
}
